import { useEffect } from "react";
import { useMatch } from "react-router-dom";

import { useCustomerLabel } from "@/hooks/useCustomers";
import { useEnergyAdvisorCustomer } from "@/hooks/useEnergyAdvisorCustomer";
import {
  setAdvisorClient,
  useAdvisorClientId,
} from "@/lib/advisor-client-context";
import { useStickyActiveModule } from "@/lib/module-context";

/**
 * The client id in the current URL, if we're on an advisor client route.
 *
 * Two matchers because `/energy-advisor/:customerId/*` does not match the
 * bare `/energy-advisor/:customerId` (the router redirects that to the first
 * tool, but the sidebar renders during that beat).
 *
 * The *record* side (`/customers/:id`) doesn't resolve here. Drilling into a
 * client from a deal, an account, or the clients list also updates the
 * picker, but that's pushed by `CustomerDetailPage` when it loads a client
 * (`useAdoptAdvisorClient`) rather than matched here — the page knows which
 * client it actually loaded, and keeping the trigger there means one obvious
 * place to look instead of a url pattern buried in this resolver.
 */
function useRouteClientId(): string | undefined {
  const nested = useMatch("/energy-advisor/:customerId/*");
  const bare = useMatch("/energy-advisor/:customerId");
  return nested?.params.customerId ?? bare?.params.customerId;
}

/**
 * Adopts `customerId` as the Energy Advisor module's scoped client — the
 * inverse direction, pushed from a client record page.
 *
 * Navigating to a client from a deal, an account, or the clients list lands
 * on `/customers/:id`, which the advisor's own resolver doesn't match. Left
 * alone, the picker kept naming whoever was in scope before, so the sidebar
 * offered one client's tools while the page showed another.
 *
 * **Only inside the Energy Advisor module.** Everywhere else there is no
 * picker to keep in sync, and rewriting the advisor's scope from a page in
 * Operations or Admin would move something the user can't see.
 *
 * Pass `undefined` while the client is still loading; nothing is adopted
 * until there's a real id, so a failed load never clears the scope.
 *
 * **Only a client the advisor workspace can open.** `customers.view` and
 * `energy_advisor.view` are scoped independently, so a client record can
 * load while the advisor workspace 404s it (e.g. the record reached from a
 * segment-scoped deal, advisor scope set to own). Adopting it anyway filled
 * the picker with a client whose every tool 404s. The advisor lookup is the
 * test; until it resolves, nothing is adopted.
 *
 * When the lookup fails the picker is **cleared**, not left alone: leaving it
 * would name the previously scoped client while the page shows this one —
 * the mismatch this hook exists to prevent. That also covers churned /
 * closed-lost clients, which the advisor lookup excludes (see
 * `useAdvisorScopedClient`); their tools 404 there too, so an empty picker is
 * the honest state.
 */
export function useAdoptAdvisorClient(customerId: string | undefined): void {
  const activeModule = useStickyActiveModule();
  const inModule = activeModule === "energy-advisor" && !!customerId;
  const { data: advisorClient, isError } = useEnergyAdvisorCustomer(
    inModule ? customerId : undefined,
  );
  const shouldAdopt = inModule && advisorClient?.id === customerId;
  const shouldClear = inModule && isError;

  useEffect(() => {
    if (shouldAdopt && customerId) setAdvisorClient(customerId);
    else if (shouldClear) setAdvisorClient(null);
  }, [shouldAdopt, shouldClear, customerId]);
}

/**
 * The client in scope for the Energy Advisor module — what the picker shows.
 *
 * **URL first, then the remembered selection.** Opening a client adopts it;
 * otherwise the client you last picked stays in scope. That persistence is
 * the point of the picker model: navigating to Accounts, Clients, or Deals
 * keeps your client in scope, and the picker is the only way to change it.
 *
 * Also adopts a client reached by deep link, so arriving at
 * `/energy-advisor/<id>/snapshot` from a shared link sets scope rather than
 * leaving the picker showing someone else.
 *
 * Returns `null` when nothing is chosen yet, or while the name is loading —
 * the picker then reads "Select a client".
 *
 * **Drives both the picker and the tool rows.** There was briefly a second,
 * URL-only hook for the rows, so they'd vanish off a client route. That was
 * the right call while the section printed its own client-name header — a
 * header on `/deals` claims you're inside that client. Once the picker became
 * the header, the claim went away: rows under a picker reading "Pearl Medical
 * Towers" are *offering* that client's tools, and hiding them left the user
 * on a shared record with a scope picker and nothing beneath it.
 */
export function useAdvisorScopedClient(): { id: string; label: string } | null {
  const routeId = useRouteClientId();
  const stickyId = useAdvisorClientId();
  const id = routeId ?? stickyId ?? undefined;

  const { data, error } = useEnergyAdvisorCustomer(id);
  // The general client record. It labels the picker when the advisor lookup
  // fails (below), and it carries the segments the sidebar's CCA Details row
  // reads from the cache (AB#8740), so it is fetched for every client in
  // scope. `useCustomerLabel` shares `useCustomer`'s cache entry without its
  // sync polling.
  const { data: fallback } = useCustomerLabel(id);

  // Adopting a route's client is a side effect, so it runs in an effect
  // rather than during render — writing to the external store mid-render
  // would notify subscribers while React is still rendering.
  //
  // Keyed on `[routeId]` alone, which is what lets the clear below stick: it
  // re-adopts only when the route's client actually changes, so clearing a
  // dead client isn't undone on the next render while still on that URL.
  useEffect(() => {
    if (routeId) setAdvisorClient(routeId);
  }, [routeId]);

  // A client the ADVISOR route can't resolve is cleared, rather than left as
  // a label whose links 404. This is what breaks an otherwise infinite loop:
  // `EnergyAdvisorCustomerPage` bounces a 404 back to `/energy-advisor`, and
  // `EnergyAdvisorEntryRoute` redirects straight into the remembered client
  // again. Clearing lands that bounce on the picker instead.
  //
  // **Only when the id came from an advisor url** (`onAdvisorRoute`), because
  // a 404 here does NOT mean the client is gone. The advisor lookup is
  // strictly narrower than the general one — `pilot-data/src/energy_advisor/
  // customers.rs` adds `lifecycle_state NOT IN ('churned','closed_lost')` and
  // `is_demo = $demo_mode`, neither of which the general
  // `customers/customer.rs` query filters on. So a churned client renders
  // fine at `/customers/:id` while 404ing here.
  //
  // Without that guard the two directions fought: opening a churned client's
  // record page inside this module ran `useAdoptAdvisorClient`, which set the
  // scope, and this effect immediately wiped it — the picker dropping the
  // client the page was showing. And it *did* reach here on that route:
  // `/customers/:id` sets no `routeId`, so `id` falls through to the sticky
  // value the adopt had just written.
  //
  // The loop only involves advisor routes, so scoping the clear to them
  // keeps the fix and drops the collision.
  const onAdvisorRoute = routeId !== undefined;
  useEffect(() => {
    if (onAdvisorRoute && id && error) setAdvisorClient(null);
  }, [onAdvisorRoute, id, error]);

  if (!id) return null;
  if (data) return { id, label: data.name };

  // The advisor lookup didn't resolve but the client may still be real — it's
  // churned/closed-lost or on the other side of demo mode (see the clear
  // effect above). Fall back to the general customer record so the picker
  // stays labeled instead of reading "Select a client" while a client is
  // demonstrably in scope. `useCustomer` is already cached whenever we got
  // here from a record page.
  if (fallback) return { id, label: fallback.name };
  return null;
}

